<template>
  <v-dialog v-model="dialog" max-width="800px" persistent>
    <v-card class="winner-dialog">
      <v-card-title class="dialog-header">
        <div class="d-flex align-center">
          <v-icon color="white" size="28" class="mr-3">mdi-trophy</v-icon>
          <h2 class="text-h5 font-weight-bold text-white mb-0">Select Winner</h2>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon @click="close" color="white" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Category Selection -->
        <div class="mb-6">
          <h3 class="text-h6 font-weight-bold mb-4">Select Category</h3>
          <v-select
            v-model="selectedCategory"
            :items="categories"
            item-text="name"
            item-value="_id"
            label="Choose a category"
            outlined
            dense
            @change="onCategoryChange"
            :loading="loadingSubcategories"
          ></v-select>
        </div>

        <!-- Subcategory Selection -->
        <div v-if="selectedCategory && availableSubcategories.length > 0" class="mb-6">
          <h3 class="text-h6 font-weight-bold mb-4">Select Subcategory</h3>
          <v-select
            v-model="selectedSubcategory"
            :items="availableSubcategories"
            item-text="name"
            item-value="_id"
            label="Choose a subcategory"
            outlined
            dense
            @change="onSubcategoryChange"
            :loading="loadingProducts"
          >
            <template v-slot:item="{ item }">
              <div class="d-flex align-center">
                <v-icon color="success" class="mr-2">mdi-tag-multiple</v-icon>
                <div>
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div class="text-caption grey--text">{{ item.productCount }} products</div>
                </div>
              </div>
            </template>
          </v-select>
        </div>

        <!-- No Available Subcategories -->
        <div v-if="selectedCategory && availableSubcategories.length === 0" class="mb-6">
          <v-alert type="info" outlined>
            <div class="d-flex align-center">
              <v-icon class="mr-2">mdi-information</v-icon>
              <div>
                <strong>No available subcategories</strong>
                <p class="mb-0">All subcategories in this category already have winners selected.</p>
              </div>
            </div>
          </v-alert>
        </div>

        <!-- Product Selection -->
        <div v-if="selectedSubcategory && selectedSubcategoryData" class="mb-6">
          <h3 class="text-h6 font-weight-bold mb-4">Select Winning Product</h3>
          <v-row>
            <v-col 
              v-for="(product, index) in selectedSubcategoryData.products" 
              :key="product._id || index"
              cols="12" 
              sm="6" 
              md="4"
            >
              <v-card 
                class="product-selection-card"
                :class="{ 'selected': selectedProduct && selectedProduct._id === product._id }"
                @click="selectProduct(product, index)"
                elevation="2"
                hover
              >
                <div class="product-image-container">
                  <v-img 
                    :src="product.image" 
                    height="120" 
                    contain 
                    class="product-image"
                  ></v-img>
                  <div v-if="selectedProduct && selectedProduct._id === product._id" class="selection-overlay">
                    <v-icon color="white" size="32">mdi-check-circle</v-icon>
                  </div>
                </div>
                <v-card-text class="text-center pa-3">
                  <p class="text-body-2 font-weight-medium product-name">{{ product.name }}</p>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>


      </v-card-text>

      <v-card-actions class="pa-6 pt-0">
        <v-spacer></v-spacer>
        <v-btn 
          text 
          @click="close"
          class="mr-3"
        >
          Cancel
        </v-btn>
        <v-btn 
          color="primary" 
          @click="saveWinner"
          :disabled="!canSave"
          :loading="saving"
        >
          <v-icon left>mdi-trophy</v-icon>
          Select Winner
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import config from '@/config.js'

export default {
  name: 'WinnerSelectionDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    categories: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dialog: false,
      selectedCategory: null,
      selectedSubcategory: null,
      selectedProduct: null,
      selectedProductIndex: null,
      selectedSubcategoryIndex: null,
      availableSubcategories: [],
      selectedSubcategoryData: null,
      loadingSubcategories: false,
      loadingProducts: false,
      saving: false
    }
  },
  computed: {
    canSave() {
      return this.selectedCategory && 
             this.selectedSubcategory && 
             this.selectedProduct && 
             !this.saving
    }
  },
  watch: {
    value(newVal) {
      this.dialog = newVal
      if (newVal) {
        this.resetForm()
      }
    },
    dialog(newVal) {
      this.$emit('input', newVal)
    }
  },
  methods: {
    resetForm() {
      this.selectedCategory = null
      this.selectedSubcategory = null
      this.selectedProduct = null
      this.selectedProductIndex = null
      this.selectedSubcategoryIndex = null
      this.availableSubcategories = []
      this.selectedSubcategoryData = null
    },

    async onCategoryChange() {
      if (!this.selectedCategory) return
      
      this.loadingSubcategories = true
      this.selectedSubcategory = null
      this.selectedProduct = null
      this.availableSubcategories = []
      this.selectedSubcategoryData = null

      try {
        const response = await fetch(`${config.API_BASE_URL}/winners/available-subcategories/${this.selectedCategory}`)
        if (!response.ok) throw new Error('Failed to fetch available subcategories')
        this.availableSubcategories = await response.json()
      } catch (error) {
        console.error('Error fetching available subcategories:', error)
        this.$emit('error', 'Failed to load available subcategories')
      } finally {
        this.loadingSubcategories = false
      }
    },

    onSubcategoryChange() {
      if (!this.selectedSubcategory) return
      
      this.selectedProduct = null
      this.selectedProductIndex = null
      this.selectedSubcategoryData = this.availableSubcategories.find(sub => sub._id === this.selectedSubcategory)
      this.selectedSubcategoryIndex = this.selectedSubcategoryData.index
    },

    selectProduct(product, index) {
      this.selectedProduct = product
      this.selectedProductIndex = index
    },

    async saveWinner() {
      if (!this.canSave) return

      this.saving = true

      try {
        const winnerData = {
          categoryId: this.selectedCategory,
          subcategoryId: this.selectedSubcategory,
          productId: this.selectedProduct._id,
          productName: this.selectedProduct.name,
          productImage: this.selectedProduct.image,
          productIndex: this.selectedProductIndex,
          subcategoryIndex: this.selectedSubcategoryIndex
        }

        const response = await fetch(`${config.API_BASE_URL}/winners`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(winnerData)
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to save winner')
        }

        const savedWinner = await response.json()
        this.$emit('save', savedWinner)
        this.close()
        
      } catch (error) {
        console.error('Error saving winner:', error)
        this.$emit('error', error.message || 'Failed to save winner')
      } finally {
        this.saving = false
      }
    },

    close() {
      this.dialog = false
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.winner-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  padding: 24px 28px;
  position: relative;
  overflow: hidden;
}

.dialog-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  pointer-events: none;
}

.close-btn {
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255,255,255,0.25);
  transform: scale(1.05);
}

.product-selection-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
}

.product-selection-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.product-selection-card.selected {
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.product-image-container {
  position: relative;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  overflow: hidden;
}

.product-image {
  transition: all 0.3s ease;
}

.product-selection-card:hover .product-image {
  transform: scale(1.05);
}

.selection-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(76, 175, 80, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.product-name {
  color: #2c3e50;
  line-height: 1.4;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}
</style>
